// src/App.js
import React from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { LogOut } from "lucide-react";
import { useAuth } from "react-oidc-context";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import PartnerOverview from "./components/dashboard/PartnerOverview";
import { PartnerProfile } from "./components/dashboard/PartnerProfile";
import { PolicyDetail } from "./components/dashboard/PolicyDetail";
import PageNotFound from "./components/dashboard/PageNotFound";

const AppHeader = ({ userInfo, handleLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const currentTab =
    location.pathname === "/" || location.pathname === "/overview"
      ? "overview"
      : location.pathname === "/policy"
      ? "policy"
      : "profile";

  return (
    <header className="bg-white border-b border-gray-200">
      <div className="w-[85%] mx-auto py-4 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <img src="/WINR-Paragon-logo-BLACK.png" alt="WINR" className="h-7 sm:h-8 md:h-10 w-auto object-contain" />
          <nav className="flex gap-2">
            <button
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                currentTab === "overview"
                  ? "bg-blue-50 text-blue-700"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
              onClick={() => navigate("/overview")}
            >
              Partner Overview
            </button>
            <button
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                currentTab === "profile"
                  ? "bg-blue-50 text-blue-700"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
              onClick={() => navigate("/partner")}
            >
              Partner Profile
            </button>
            <button
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                currentTab === "policy"
                  ? "bg-blue-50 text-blue-700"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
              onClick={() => navigate("/policy")}
            >
              Policy Detail
            </button>
          </nav>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-600">
            {userInfo?.name || userInfo?.email}
          </span>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </div>
    </header>
  );
};

// PrivateRoute wrapper
const PrivateRoute = ({ children }) => {
  const auth = useAuth();
  if (auth.isLoading){
    <div className="fixed inset-0 bg-white/70 flex items-center justify-center z-50">
      <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
  }
 
  if (!auth.isAuthenticated){
    auth.signinRedirect();
  }
  return children;
};

function App() {
  const auth = useAuth();

  const handleLogout = async () => {
    try {
      const isDev = process.env.REACT_APP_ENV === "dev";
      
      await auth.signoutRedirect({
            extraQueryParams: {
                client_id: "4ddg2f8q9gucu6tsghni1it2sj", 
                redirect_uri: isDev ? "http://localhost:3000" : "https://paragon.winrdata.com",
                response_type: 'code'
            },
        });
      
    } catch (err) {
      console.error("Logout failed", err);
    }
  };

  if (auth.isLoading) {
    return (
      <div className="fixed inset-0 bg-white/70 flex items-center justify-center z-50">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ToastContainer position="top-right" autoClose={4000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
      {auth.isAuthenticated && (
        <AppHeader userInfo={auth.user?.profile} handleLogout={handleLogout} />
      )}

      <main className="w-[85%] mx-auto py-8">
        <Routes>

          {/* Redirect root */}
          <Route
            path="/"
            element={
              <PrivateRoute>
                <PartnerOverview />
              </PrivateRoute>
            }
          />

          {/* Partner Overview */}
          <Route
            path="/overview"
            element={
              <PrivateRoute>
                <PartnerOverview />
              </PrivateRoute>
            }
          />

          {/* Partner Profile */}
          <Route
            path="/partner"
            element={
              <PrivateRoute>
                <PartnerProfile />
              </PrivateRoute>
            }
          />

          <Route
            path="/partner/:partnerName"
            element={
              <PrivateRoute>
                <PartnerProfile />
              </PrivateRoute>
            }
          />


          {/* Policy Detail */}
          <Route
            path="/policy"
            element={
              <PrivateRoute>
                <PolicyDetail />
              </PrivateRoute>
            }
          />

          {/* fallback */}
          <Route
            path="*"
            element={
              <PrivateRoute>
                <PageNotFound />
              </PrivateRoute>
            }
          />
        </Routes>
      </main>
    </div>
  );
}

export default App;
